import React, { useEffect, useState } from 'react'
import IntroducedCompetitor from './IntroducedCompetitor'

// Define interfaces for the component props
interface Competitor {
  id: string;
  name: string;
  image: string;
  points: number;
  bugs: number;
  role?: string;
  coins?: number;
  /* Index signature keeps this interface compatible with other
     `Competitor` variants used across the Winner module that may
     include additional dynamic properties (avoids TS2719 error). */
  [key: string]: unknown;
}

interface CompetitorStyles {
  [competitorId: string]: {
    borderColor?: string;
    rgbValues?: number[];
  };
}

interface CompetitorIntroductionGridProps {
  competitors: Competitor[];
  onAnimationComplete: () => void;
  competitorStyles: CompetitorStyles;
}

type AnimationState = 'entering' | 'visible' | 'exiting';

const CompetitorIntroductionGrid: React.FC<CompetitorIntroductionGridProps> = ({
  competitors,
  onAnimationComplete,
  competitorStyles
}) => {
  const [animationState, setAnimationState] = useState<AnimationState>('entering');

  useEffect(() => {
    // Schedule animation states

    // After all competitors have entered, show them for 2 seconds
    const visibleTimer: NodeJS.Timeout = setTimeout(() => {
      setAnimationState('visible');
    }, (competitors.length * 300) + 500);

    // Then start exit animations
    const exitTimer: NodeJS.Timeout = setTimeout(() => {
      setAnimationState('exiting');
    }, (competitors.length * 300) + 2500);

    // After all exit animations, notify parent
    const completeTimer: NodeJS.Timeout = setTimeout(() => {
      onAnimationComplete();
    }, (competitors.length * 2 * 300) + 3000);

    return () => {
      clearTimeout(visibleTimer);
      clearTimeout(exitTimer);
      clearTimeout(completeTimer);
    };
  }, [competitors.length, onAnimationComplete]);

  return (
    <div className="absolute inset-0 flex items-center justify-center z-20">
      <div className="flex flex-wrap justify-center items-center max-w-[900px]">
        {competitors.map((competitor, index) => (
          <IntroducedCompetitor
            key={`grid-intro-${competitor.id}`}
            competitor={competitor}
            index={index}
            animationState={animationState}
            borderColor={competitorStyles[competitor.id]?.borderColor || '#8a2be2'}
          />
        ))}
      </div>
    </div>
  );
};

export default CompetitorIntroductionGrid;
